import { getState } from '../analytics-state';
import {
  STASH, NOT_APPLICABLE, ATC, PRODUCT_POD, SECTION, CART_ADD, CART_ADD_RECS
} from '../constants';

// FSDBM / FPN "Find More Eligible Items" overlay (KLQZ-1749).
// The overlay's add-to-cart rides cartItem.DD_EVENT as a bare 'cart add' with no
// recommendation stash, which left eVar53 blank. We synthesize the spec-compliant
// 'recommendation cart addition' stash (FSDBM tech spec use case 6) when the added
// SKU matches a product pod that mounted inside the FSDBM drawer.
const DEALS = 'deals';

// FSDBM drawer pods register via product-pod-v7.load into `productpods` state with
// this parent. Matching on it scopes this strictly to the FSDBM drawer and never to
// the cart's own recs carousels (which share the same cartItem.DD_EVENT 'cart add'
// signature and are handled by the product-pod listeners).
const FPN_POD_PARENT = 'fsdbm-eligible-items';

const getSku = (item = {}) => (
  item?.productInfo?.sku || item?.sku || item?.SKU || ''
).toString();

// Builds the 'recommendation cart addition' stash event for items added from the FSDBM
// drawer. eVar53 is derived from the top-level stash (component/section/target/
// pageType/sharedSection) via formatATCLocation in daMap; the top-level
// stash.recommendation feeds the recommendation eVars. Returns null when the add did
// not originate from the FSDBM drawer (no matching fsdbm-eligible-items pod).
const buildFpnRecsCartAdditionEvent = (items = []) => {
  const { productpods = [] } = getState() || {};
  const findPod = (sku) => (sku
    ? productpods.find((pod) => pod?.parent === FPN_POD_PARENT && pod?.itemId?.toString() === sku) || null
    : null);

  const matched = items
    .map((item) => {
      const sku = getSku(item);
      return { sku, pod: findPod(sku) };
    })
    .filter((entry) => entry.sku && entry.pod);

  if (!matched.length) return null;

  const currentPageType = digitalData?.page?.pageInfo?.pageType || '';
  const primaryPod = matched[0].pod;

  return {
    eventInfo: {
      eventName: CART_ADD_RECS
    },
    category: {
      primaryCategory: STASH
    },
    eventMode: STASH,
    stash: {
      component: PRODUCT_POD,
      item: matched.map(({ sku, pod }) => ({
        index: pod?.position ?? '',
        productID: sku
      })),
      pageType: currentPageType,
      section: SECTION,
      sharedSection: NOT_APPLICABLE,
      target: ATC,
      recommendation: {
        recommendationType: DEALS,
        pageType: currentPageType,
        displayPosition: primaryPod?.position ?? '',
        anchorProductSku: NOT_APPLICABLE,
        // Spec: strategy = API "version", recommendationVersion = API "pversion".
        // The fsdbm-eligible-items pod payload does NOT currently carry these, so they
        // resolve to n/a. They populate automatically once the component includes
        // version/pversion in the product-pod-v7.load payload (FE gap; spec 6.1 blank).
        strategy: primaryPod?.version ?? NOT_APPLICABLE,
        recommendationVersion: primaryPod?.pversion ?? NOT_APPLICABLE,
        featureVersion: NOT_APPLICABLE,
        interval: NOT_APPLICABLE
      }
    }
  };
};

 const sponsoredTracking = (sponsoredClickBeacons) => {
    const validBeacons = sponsoredClickBeacons.filter(Boolean);
    const showEvents = typeof window !== 'undefined' && window.location.search.indexOf('showEvents=true') !== -1;

    if (validBeacons.length === 0) {
      if (showEvents) {
        console.warn('No valid tracking links provided');
      }
      return;
    }
    validBeacons.forEach((trackingLink) => {
      window.fetch(trackingLink, {
        method: 'GET',
        keepalive: true,
      })
        .catch((error) => {
          if (showEvents) {
            console.error(`Error occurred while triggering event for:${trackingLink}`, error);
          }
        });
    });
  };

  const sponsoredClickThroughAnalytics = (sponsoredMetadata) => {
      const sponsoredPodClickThroughEvent = {
        category: {
          primaryCategory: 'interaction'
        },
        eventInfo: {
          eventName: 'content media click'
        },
        creativeID: null,
        placementID: sponsoredMetadata?.placementId,
        campaignID: sponsoredMetadata?.campaignId,
        slotID: sponsoredMetadata?.slotId,
        sponsoredID: sponsoredMetadata?.sponsoredId,
        trackSource: sponsoredMetadata?.trackSource,
      };
  
      digitalData.event.push(sponsoredPodClickThroughEvent);
    };
  

export const cartItem = () => {
  window.LIFE_CYCLE_EVENT_BUS.on('cartItem.DD_EVENT', ({ output = {} }) => {
    const eventObj = output;
    const { isSponsored, sponsoredBeacon, sponsoredMetadata } = eventObj?.sponsoredInfo || {};
    const { onClickBeacons, onClickBeacon } = sponsoredBeacon || {};
    const sponsoredClickBeacons = onClickBeacons || [onClickBeacon];
    const sponsoredProduct = !!(isSponsored);
    if (eventObj) {
      let eventObject = {
        category: {
          primaryCategory: eventObj?.primaryCategory
        },
        eventInfo: {
          eventName: eventObj?.eventName
        }
      };
      if (eventObj?.eventMode) {
        eventObject.eventMode = eventObj.eventMode;
      }
      if (eventObj?.myAccount?.settings) {
        eventObject.myAccount = {};
        eventObject.myAccount.settings = eventObj.myAccount.settings;
      }
      if (eventObj?.displayedProtectionPlan) {
        eventObject.componentInfo = {};
        eventObject.componentInfo.displayedProtectionPlan = eventObj.displayedProtectionPlan;
      }

      if (eventObj?.eventName) {
        if (eventObj?.eventName === 'cart add') {
          if (eventObj?.isProjectShopping) {
            eventObject.eventInfo.abTest = 'mycart drawer ps';
          } else {
            eventObject.eventInfo.abTest = eventObj?.isDrawer ? 'mycart drawer v1' : 'mycart v1';
          }
        }
        if ((eventObj?.eventName === 'cart add') || (eventObj?.eventName === 'cart remove')) {
          if (eventObj?.packageID) {
            eventObject.eventInfo.packageID = eventObj.packageID;
          }
        }
        if (eventObj?.eventName === 'hdpp') {
          eventObject.eventInfo.abTest = eventObj?.isDrawer ? 'mycart drawer v1' : 'mycart v1';
        }
      }
      if (eventObj?.item) {
        if (eventObj?.item?.length) {
          eventObject.item = eventObj.item;
        } else {
          eventObject.item = [];
          eventObject.item.push(eventObj.item);
        }
      } else if (eventObj?.stash) {
        eventObject.stash = eventObj.stash;
      } else if (eventObj?.quote) {
        if (eventObj?.quote?.item && eventObj?.quote?.item?.length > 0) {
          eventObject.quote = eventObj.quote;
        }
      }
      if (eventObj?.overlayLocation) {
        eventObject.overlayLocation = eventObj.overlayLocation;
      }
      if (eventObj?.removeLocation) {
        eventObject.removeLocation = eventObj.removeLocation;
      }
      if (eventObj?.attributes) {
        eventObject.attributes = eventObj.attributes;
      }
      if (typeof eventMessage !== 'undefined') {
        eventObject.eventInfo.eventMessage = eventObj.eventInfo.eventMessage;
      }
      if (eventObj.error) {
        eventObject.eventInfo.error = [];
        eventObject.eventInfo.error.push(eventObj.error);
      }
      if (eventObj?.content) {
        eventObject.content = eventObj.content;
      }
      if (eventObj.paymentMethod) {
        eventObject.paymentMethod = eventObj.paymentMethod;
      }
      if (eventObj?.cart) {
        eventObject.cart = eventObj.cart;
      }
      if (eventObj?.cartQuantity) {
        eventObject.cartQuantity = eventObj.cartQuantity;
      }
      if (eventObj?.itemId) {
        eventObject.itemId = eventObj.itemId;
      }
      if (eventObj?.emptyCart) {
        eventObject.emptyCart = eventObj.emptyCart;
      }
      if (eventObj?.subContent !== null && eventObj?.subContent !== undefined) {
        eventObject.subContent = eventObj.subContent;
      }
      if (eventObj?.cartViewType) {
        eventObject.cartViewType = eventObj.cartViewType;
      }
      if (eventObj?.bulkAction !== null && eventObj?.bulkAction !== undefined) {
        eventObject.bulkAction = eventObj.bulkAction;
      }
      if (typeof output?.validate === 'function') {
        eventObject = output.validate(eventObject) || eventObject;
      }

      // FSDBM drawer add-to-cart: emit the recommendation stash BEFORE the cart add so
      // daMap stores recCartAddLoc for the cart add beacon to consume (KLQZ-1749).
      // buildFpnRecsCartAdditionEvent returns null unless the SKU maps to an
      // fsdbm-eligible-items pod, so this is a no-op for all other cart adds.
      if (eventObj?.eventName === CART_ADD && eventObject.item?.length) {
        const fpnRecsEvent = buildFpnRecsCartAdditionEvent(eventObject.item);
        if (fpnRecsEvent) {
          digitalData.event.push(fpnRecsEvent);
        }
      }

      digitalData.event.push(eventObject);

      if (sponsoredProduct && sponsoredClickBeacons.length > 0) {
        sponsoredTracking(sponsoredClickBeacons);
      }

      if (sponsoredProduct && sponsoredMetadata) {
      sponsoredClickThroughAnalytics(sponsoredMetadata);
    }

    }
  });
};